import type { Store } from '@reduxjs/toolkit';

import type { Message, StateWithSidekickChat } from '../state/chatTypes';

type ChatSnapshot = Partial<StateWithSidekickChat>;

type ChatStore = Store<ChatSnapshot>;

// A missing chat slice reads as an empty chat.
const readLatestQuestion = (state: ChatSnapshot): Message | undefined => {
  const messages = state.aiSidekickChat?.messages ?? [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === 'user') return messages[index];
  }
  return undefined;
};

/**
 * Id of the newest question in the chat.
 * @param state - The store state to read
 * @returns The newest user message's id, or `undefined` when there is none
 */
export const readLatestQuestionId = (state: ChatSnapshot): string | undefined =>
  readLatestQuestion(state)?.id;

// A question is in flight while it waits in the host queue or while its
// submission streams, which covers creating the conversation.
const isQuestionInFlight = (state: ChatSnapshot): boolean => {
  const chat = state.aiSidekickChat;
  return (
    chat !== undefined &&
    (chat.isStreaming || chat.pendingHostUserMessage !== null)
  );
};

type WatchQuestionSentParams = {
  store: ChatStore;
  /** The question that was submitted. */
  question: string;
  /** Newest question id before the submission. */
  baselineQuestionId: string | undefined;
  /** Runs once the question lands in the chat. */
  onSent: () => void;
};

/**
 * Waits for an accepted submission's question to land in the chat.
 * Watching ends without `onSent` when a different question lands, the
 * chat is reset, or the submission settles without adding the question,
 * as when its conversation could not be created.
 * @returns Stops watching.
 */
export const watchQuestionSent = ({
  store,
  question,
  baselineQuestionId,
  onSent,
}: WatchQuestionSentParams): (() => void) => {
  const trimmedQuestion = question.trim();
  let isWatching = true;
  let unsubscribe: () => void = () => undefined;
  const stop = () => {
    isWatching = false;
    unsubscribe();
  };

  const check = () => {
    if (!isWatching) return;
    const state = store.getState();
    const latest = readLatestQuestion(state);
    if (latest?.id !== baselineQuestionId) {
      stop();
      if (latest?.content === trimmedQuestion) onSent();
      return;
    }
    if (!isQuestionInFlight(state)) stop();
  };

  unsubscribe = store.subscribe(check);
  check();
  return stop;
};
